import { Box, FormHelperText } from "@mui/material";
import { styled } from "@mui/system";
import { ChangeEvent } from "react";
import CustomLabel from "./custom-label";

const blue = {
  100: "#DAECFF",
  200: "#b6daff",
  400: "#3399FF",
  500: "#007FFF",
  600: "#0072E5",
  900: "#003A75",
};

const grey = {
  50: "#F3F6F9",
  100: "#E5EAF2",
  200: "#DAE2ED",
  300: "#C7D0DD",
  400: "#B0B8C4",
  500: "#9DA8B7",
  600: "#6B7A90",
  700: "#434D5B",
  800: "#303740",
  900: "#1C2025",
};

const StyledTextarea = styled("textarea")(
  ({ theme }) => `
    box-sizing: border-box;
    width: 100%;
    height: 100px;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.5;
    padding: 8px 12px;
    border-radius: 4px;
    color: ${theme.palette.mode === "dark" ? grey[300] : grey[900]};
    background: ${theme.palette.mode === "dark" ? grey[900] : "#fff"};
    border: 1px solid ${theme.palette.mode === "dark" ? grey[700] : grey[200]};
    box-shadow: 0px 2px 2px ${
      theme.palette.mode === "dark" ? grey[900] : grey[50]
    };
    resize: none;

    &:hover {
        border:"none";
    }

    &:focus {
        border:"none";
      box-shadow: 0 0 0 1px ${
        theme.palette.mode === "dark" ? blue[600] : blue[100]
      };
    }

    // firefox
    &:focus-visible {
      outline: 0;
      border:"none";
    }
  `,
);

export default function Textarea({
  placeholder,
  defaultValue,
  value,
  onChange,
  onValueChange,
  isError,
  errorMsg,
  label,
}: {
  placeholder?: string;
  defaultValue?: string;
  value?: string | null;
  isError?: boolean;
  errorMsg?: string;
  onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  /** Callback that receives the processed value (empty strings are converted to null) */
  onValueChange?: (value: string | null) => void;
  label?: string;
}) {
  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onChange?.(e);
    if (onValueChange) {
      const val = e.target.value;
      onValueChange(val === "" ? null : val);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      {label && (
        <CustomLabel
          sx={(theme) => theme.nawy.typography.english.variants.body.xs.regular}
        >
          {label}
        </CustomLabel>
      )}
      <StyledTextarea
        value={value ?? defaultValue ?? ""}
        onChange={handleChange}
        rows={6}
        placeholder={placeholder}
      />
      {isError && <FormHelperText error>{errorMsg}</FormHelperText>}
    </Box>
  );
}
